/**
 * Dealer-facing Manual offer card (no rate, no lender name).
 * Shown only when there are no public matched offers and a publish blob exists.
 */
import { Badge } from "@/components/ui/badge";

export type DealerManualOfferCard = {
  label?: string;
  term_months?: number | null;
  payment?: number | null;
  down_payment_pct?: number | null;
  down_payment_amount?: number | null;
  notes?: string;
  published_at?: string;
};

type Props = {
  card: DealerManualOfferCard;
};

export function ManualOfferDealerSection({ card }: Props) {
  const payment =
    card.payment != null && !Number.isNaN(Number(card.payment))
      ? Number(card.payment).toLocaleString(undefined, { style: "currency", currency: "USD" })
      : null;
  const downPct =
    card.down_payment_pct != null && !Number.isNaN(Number(card.down_payment_pct))
      ? `${Number(card.down_payment_pct)}%`
      : null;

  return (
    <div className="mt-4 space-y-2">
      <p className="text-sm font-medium text-muted-foreground mb-2">Manual offer</p>
      <div className="p-3 border rounded-lg border-amber-200 bg-amber-50/40 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="font-medium">{card.label || "Manual offer"}</span>
          <Badge variant="secondary">View only</Badge>
        </div>
        <div className="text-sm text-muted-foreground space-x-2">
          {card.term_months != null ? <span>Term: {card.term_months} mo</span> : null}
          {payment ? <span>· Payment: {payment}</span> : null}
          {downPct ? <span>· Down: {downPct}</span> : null}
        </div>
        {card.notes ? <p className="text-xs text-muted-foreground italic">{card.notes}</p> : null}
        <p className="text-[11px] text-muted-foreground">
          Your account manager will submit this program if you proceed. Rate is not shown on this card.
        </p>
      </div>
    </div>
  );
}

/** True when public prequal has no matched offers for dealer display. */
export function hasPublicMatchedOffers(prequalResults: any): boolean {
  if (!prequalResults || typeof prequalResults !== "object") return false;
  const display = prequalResults.display_offers;
  if (Array.isArray(display) && display.length > 0) return true;
  const results = prequalResults.results;
  if (!Array.isArray(results)) return false;
  return results.some((r: { matched?: boolean }) => r.matched);
}
